import { useEffect, useRef } from "react";
import { UserCheck, X } from "lucide-react";
import { SectionCard } from "@/components/SectionCard";
import { AuswahlMitSuche } from "@/components/AuswahlMitSuche";
import { useRoleMembersAvailable } from "@/api/queries/roles";
import { useAvailableUsers } from "@/api/queries/users";
import { useUserContext, darfJetzt } from "@/auth/operations";
import type { EntscheiderWahl, InstanceGateView, ProcessInstanceView } from "@/types";
import {
  CHIP_NICHT_IN_ROLLE,
  CHIP_NICHT_VERFUEGBAR,
  INHABER_ZWANG_TEXT,
  KEIN_ADRESSBUCH_TEXT,
  NAMEN_UNBEKANNT_TEXT,
  PERSONEN_LADEN_TEXT,
  WIEDERHOLEN_LAEUFT_TEXT,
  WIEDERHOLEN_TEXT,
  aufgeloesteAnzahl,
  entscheiderSatz,
  gewaehlteAnzahl,
  gleicheWahl,
  inhaberAufgeloest,
  inhaberIstGesetzt,
  inhaberIstWahl,
  keineWahlText,
  personenWahlGetroffen,
  personenWahlLabel,
  quorumFehlt,
  quorumSatz,
  rollenMitgliedLabel,
  wirksameWahl,
} from "./entscheiderSatz";

// #330 (UC-1): „Wer entscheidet" an der Einrichtung.
//
// Die Vorlage steckt den RAHMEN (welche Fach-Rolle, ob der Inhaber selbst darf, ob auf
// bestimmte Personen eingeschränkt werden darf); die Einrichtung wählt DARIN. Genau das
// zeigt die Karte: je Freigabe-Schritt eine Zeile mit dem Vorschau-Satz, und Bedien-Elemente
// ausschliesslich dort, wo die Vorlage sie freigegeben hat.
//
// Drei Dinge, die man beim Erweitern nicht brechen darf:
//  · Ein Schalter, den die Vorlage NICHT freigegeben hat, bekommt kein Bedien-Element —
//    weder gesperrt noch sichtbar. Er ist keine Wahl dieser Ebene.
//  · Personen werden AUSGEWÄHLT, nie getippt (Haus-Regel „Auswahl statt Tippen"). Eine
//    getippte Id wäre nicht überprüfbar und liefe in ein generisches 400 des Masters.
//  · Die Rollen-Id kommt AUSSCHLIESSLICH aus `gate.rolleId` (Fix-Runde 1, MED-6/MED-8). Sie
//    aus dem Namen zu erraten hiesse, die Normalisierung des Masters nachzubauen — und
//    zwänge nebenbei jeden Nicht-Admin durch die admin-only Rollen-Liste.

export function EntscheiderCard({
  instance,
  /** Nur die in DIESER Sitzung berührten Schritte — der Rest bleibt unangetastet. */
  beruehrt,
  onChange,
  /** Präventiv-Meldungen je stepId (aus `entscheiderFehler`, gerechnet auf der Seite). */
  fehler,
  /** Meldet die gelesene Mitglieder-Menge nach oben — die Seite braucht sie zum Zählen. */
  onMitglieder,
  darfEdit,
}: {
  instance: ProcessInstanceView;
  beruehrt: Record<string, EntscheiderWahl>;
  onChange: (stepId: string, wahl: EntscheiderWahl) => void;
  fehler: Record<string, string>;
  onMitglieder: (stepId: string, userIds: string[]) => void;
  darfEdit: boolean;
}) {
  // ⚠ DREI Zustände, drei Bedeutungen (Muster `effectivePlugins`, #320):
  //  · `undefined` = älterer Master ⇒ die Karte erscheint GAR NICHT. Eine leere Liste wäre
  //    die Behauptung „dieser Prozess hat keine Freigaben", und die kann die Oberfläche
  //    nicht belegen.
  //  · `null` = die Definition ist nicht lesbar ⇒ die Karte erscheint und sagt genau das.
  //    Gewählt werden kann nichts: auf einer unbekannten Schritt-Liste darf nichts
  //    aufgebaut werden, und der Master ersetzt die Sammlung voll.
  //  · `[]` = wirklich keine Freigabe-Schritte ⇒ nichts zu zeigen.
  const unlesbar = instance.gates === null;
  const gates = instance.gates ?? [];
  // #332 (Fix-Runde 1, L-6): das Adressbuch hängt an einem eigenen Vorgang — wer ihn nicht
  // hat, bekommt keinen Picker angeboten, der ihm nur ein 403 einbrächte. ⚠ Der Hook läuft
  // VOR dem frühen Ausstieg (Regeln der Hooks); ein dem Server unbekannter Vorgang gilt als
  // erlaubt (#299-Skew-Regel: anzeigen und den Server entscheiden lassen).
  //
  // ⚠ `darfJetzt` statt `darfVorgang` (Fix-Runde 2, MED-B): SOLANGE der Kontext lädt, wird
  // nichts abgerufen — sonst liefe der erste Abruf gegen ein noch unbekanntes Recht.
  // Verneint wird erst, wenn der Kontext GELADEN ist (`ctx.isSuccess`); vorher darf die
  // Karte keine zweite, widersprüchliche Erklärung zeigen.
  const ctx = useUserContext();
  const darfAdressbuch = darfJetzt(ctx, "user-directory.browse");
  const adressbuchVerweigert = ctx.isSuccess && !darfAdressbuch;
  if (instance.gates === undefined || (gates.length === 0 && !unlesbar)) return null;

  return (
    <SectionCard
      icon={<UserCheck className="h-[18px] w-[18px]" />}
      title="Wer entscheidet"
      description="Die Vorlage gibt den Rahmen vor; hier wird für diese Einrichtung gewählt, wen ein Freigabe-Schritt fragt."
      testId="instance-entscheider-card"
    >
      {unlesbar ? (
        <p className="text-[12.5px] text-warn" data-testid="instance-entscheider-unlesbar">
          Die Definition des Prozesses ist gerade nicht abrufbar — welche Freigabe-Schritte es
          gibt und wer sie entscheidet, lässt sich hier nicht sagen. Solange bleibt die Wahl
          unverändert.
        </p>
      ) : (
        <>
          <div className="space-y-4">
            {gates.map((gate) => (
              <GateZeile
                // ⚠ LOW-2 (Fix-Runde 4): die Einrichtung gehört in den Schlüssel. Die Zeile
                // hält einen Fehler-Latch (und die Auswahl ihren offenen Zustand) — beim
                // Wechsel A→B trüge sie sonst das Scheitern der VORIGEN Einrichtung weiter
                // und meldete für B „nicht abrufbar", während dort noch geladen wird.
                key={`${instance.id}:${gate.stepId}`}
                gate={gate}
                instanceId={instance.id}
                ownerId={instance.ownerId}
                wahl={wirksameWahl(gate, beruehrt, instance.entscheiderOverrides)}
                gespeichert={instance.entscheiderOverrides?.[gate.stepId]}
                beruehrtHier={beruehrt[gate.stepId] !== undefined}
                fehler={fehler[gate.stepId]}
                darfEdit={darfEdit}
                darfAdressbuch={darfAdressbuch}
                adressbuchVerweigert={adressbuchVerweigert}
                onChange={(w) => onChange(gate.stepId, w)}
                onMitglieder={onMitglieder}
              />
            ))}
          </div>
          <p className="mt-4 text-[12px] text-text3">
            Ohne eigene Wahl gilt, was die Vorlage vorsieht. Wer welcher Fach-Rolle angehört,
            wird in der Rollen-Verwaltung gepflegt — hier wird daraus nur ausgewählt.
            {darfEdit && " Übernommen wird die Wahl erst mit dem Speichern der Konfiguration darüber."}
          </p>
        </>
      )}
    </SectionCard>
  );
}

function GateZeile({
  gate,
  instanceId,
  ownerId,
  wahl,
  gespeichert,
  beruehrtHier,
  fehler,
  darfEdit,
  darfAdressbuch,
  adressbuchVerweigert,
  onChange,
  onMitglieder,
}: {
  gate: InstanceGateView;
  instanceId: string;
  ownerId: string;
  wahl: EntscheiderWahl;
  gespeichert: EntscheiderWahl | undefined;
  beruehrtHier: boolean;
  fehler: string | undefined;
  darfEdit: boolean;
  darfAdressbuch: boolean;
  adressbuchVerweigert: boolean;
  onChange: (wahl: EntscheiderWahl) => void;
  onMitglieder: (stepId: string, userIds: string[]) => void;
}) {
  // ⚠ #331/#332: die freie Personen-Wahl braucht KEINE Rolle mehr — allein der Schalter
  // entscheidet. Der Inhaber-Schalter ist dagegen nur dort eine Wahl, wo es etwas gibt, das
  // er ergänzen könnte (Rolle ODER freie Wahl); an einem Schritt ohne beides ist er die
  // Vorgabe des Modells, und ein Kästchen dafür liefe ins Leere (Live-Fund spike107).
  const personenWahl = gate.personenWahlErlaubt === true;
  const gewaehltePersonen = Array.isArray(wahl.personen) ? wahl.personen : [];
  // ⚠⚠ HIGH-1 (Fix-Runde 1): am rollenlosen Schritt OHNE Personen-Wahl ist der Inhaber
  // GESETZT, nicht gewählt — der Master ruft ihn unabhängig von `wahl.inhaber`. Ein
  // abwählbares Kästchen behauptete dort das Gegenteil dessen, was passiert.
  const inhaberZwang = inhaberIstGesetzt(gate, wahl);
  // ⚠ ZWEI verschiedene Fragen, die man nicht zusammenlegen darf:
  //  · SICHTBAR — gibt es an diesem Schritt überhaupt einen Inhaber-Schalter? (Sonst reine
  //    Fakten-Zeile: rollenlos ohne freie Wahl hat gar nichts zu zeigen.)
  //  · BEDIENBAR — darf der Mensch ihn umlegen? Im Zwang ist er sichtbar (angehakt, mit
  //    Begründung), aber gesperrt; und nur dann geht `inhaber` über die Leitung.
  const inhaberSichtbar = gate.inhaberErlaubt === true && (gate.rolle !== null || personenWahl);
  const inhaberWahl = inhaberIstWahl(gate, wahl);
  // ⚠⚠ HIGH-A: die Anzeige folgt der EINEN Ableitung, die `resolveEntscheider` spiegelt —
  // kein „geerbtes" `gate.inhaberGewaehlt`, sobald Personen im Spiel sind (sonst zeigte die
  // Karte einen Inhaber, den der Patch gar nicht trägt).
  //
  // Der Skew-Rückfall auf den Server-Fakt bleibt genau dort, wo er wahr ist: es gibt keinen
  // eigenen Eintrag (`inhaber === undefined`) UND keine Personen-Wahl — dann IST der Fakt der
  // gespeicherte Stand (ein Master, der die Fakten ohne passenden Eintrag liefert, hätte hier
  // sonst ein leeres Kästchen an einem Schritt, den der Inhaber längst selbst entscheidet).
  const inhaberAn =
    inhaberAufgeloest(gate, wahl) ||
    (wahl.inhaber === undefined && !personenWahlGetroffen(gate, wahl) && gate.inhaberGewaehlt === true);
  const eingeschraenkt =
    wahl.personen !== undefined ? Array.isArray(wahl.personen) : gate.personenGewaehlt !== null;

  // ⚠⚠ Personen-Quelle ist seit #331 das FIRMEN-ADRESSBUCH, nicht mehr die Rolle: das Setup
  // darf beliebige interne Personen benennen, die Rollen-Mitglieder sind darin nur MARKIERT.
  const rolleId = gate.rolleId ?? undefined;
  const verzeichnis = useAvailableUsers({ instanceId, roleId: rolleId, enabled: personenWahl && darfAdressbuch });
  // Skew-Rückfall: kennt der Master `users/available` noch nicht, bleibt wenigstens die
  // Rollen-Mitglieder-Liste — dann ist die Auswahl enger als das Modell erlaubt, und genau
  // das wird an der Zeile GESAGT (statt still weniger anzubieten).
  // ⚠⚠ FEHLER-LATCH: `refetch()` setzt den Abruf zurück auf „lädt" (nicht auf „Fehler") —
  // ohne Latch fiele die Rückfall-Liste weg UND die Zeile kippte in den Lade-Zustand, samt
  // der Schaltfläche, die man gerade angeklickt hat. Das gilt für BEIDE Wege: mit Rolle
  // (Rückfall) wie ohne (nur der Fehler-Zweig, MED-2). Gelingt das Adressbuch, fällt der
  // Latch — und mit ihm der Rückfall.
  const verzeichnisGescheitert = useRef(false);
  if (verzeichnis.isError) verzeichnisGescheitert.current = true;
  else if (verzeichnis.data) verzeichnisGescheitert.current = false;
  const rueckfall = useRoleMembersAvailable(rolleId, {
    instanceId,
    enabled: personenWahl && !!rolleId && verzeichnisGescheitert.current,
  });
  // ⚠ Der Warnsatz beschreibt, WAS gerade in der Liste steht — nicht, ob ein Abruf gescheitert
  // ist. `!verzeichnis.data` deckt damit auch MED-1 ab (ein gescheiterter REFETCH mit noch
  // vorhandenen Daten darf keinen Warnsatz über die volle Liste setzen), und der Satz bleibt
  // während einer Wiederholung stehen, statt kurz zu verschwinden.
  const nurRollenMitglieder = !verzeichnis.data && !!rueckfall.data;
  const liste: Array<{ userId: string; anzeigeName: string; mitgliedDerRolle: boolean }> =
    verzeichnis.data ?? (rueckfall.data ?? []).map((m) => ({ ...m, mitgliedDerRolle: true }));
  // ⚠⚠ HIGH-2 (Fix-Runde 1): `bekannt` ist das ADRESSBUCH — im Rückfall kennen wir es NICHT.
  // Die Rollen-Liste dafür zu halten machte aus jeder legitimen Nicht-Rollen-Person ein
  // „nicht mehr verfügbar", meldete eine zu enge Menge nach oben und sperrte über den
  // Quorum-Fehler das Speichern. `null` heisst hier ehrlich: wir wissen es nicht.
  const bekannt = verzeichnis.data ? verzeichnis.data.map((m) => m.userId) : null;
  const nameNachId = new Map(liste.map((m) => [m.userId, m.anzeigeName] as const));
  // ⚠ MED-2: markiert wird nur, wenn eine Rollen-ID MITGESCHICKT wurde — ohne sie liefert der
  // Master überall `mitgliedDerRolle: false`, und jeder Chip trüge „(nicht in der Rolle)".
  const markierungMoeglich = !!verzeichnis.data && !!rolleId && gate.rolle !== null;
  const inRolle = new Set(liste.filter((m) => m.mitgliedDerRolle).map((m) => m.userId));
  // ⚠ Drei Zustände statt zwei (Fix-Runde 1, MED-7): „lädt" ist keine Aussage. Nur ein
  // wirklich fehlgeschlagener Abruf darf „nicht abrufbar" heissen — und solange der Rückfall
  // noch läuft, ist auch nichts entschieden.
  // ⚠ „lädt" heisst: es gibt NICHTS zu zeigen und etwas ist unterwegs. Sobald eine Liste da
  // ist (auch die des Rückfalls), bleibt sie stehen — sonst verschwände sie bei jeder
  // Wiederholung samt der Schaltfläche, die sie ausgelöst hat.
  const habenListe = !!verzeichnis.data || !!rueckfall.data;
  // ⚠ MED-2: der Fehler-Zweig hängt am LATCH, nicht am flüchtigen `isError` — sonst
  // verschwindet er (und die Wiederholung darin) beim ersten Klick auf ebendiese
  // Wiederholung. „lädt" gilt nur, solange noch gar nichts gescheitert ist.
  // ⚠ NIT-1: auch WÄHREND des Rückfall-Abrufs steht „lädt" da — sonst bliebe der Slot leer
  // (das Adressbuch ist schon gescheitert, die Rollen-Liste noch unterwegs).
  const laedt =
    personenWahl &&
    !habenListe &&
    (rueckfall.isLoading || (!verzeichnisGescheitert.current && verzeichnis.isLoading));
  const nichtAbrufbar =
    personenWahl && verzeichnisGescheitert.current && !habenListe && !rueckfall.isLoading;

  // Die gelesene Menge geht nach oben: nur damit zählt der Präventiv-Fehler dieselben
  // Menschen wie der Master. ⚠ NUR aus dem Adressbuch — die Rollen-Liste des Rückfalls wäre
  // eine zu enge Aussage und erzeugte falsche Quorum-Fehler (HIGH-2).
  useEffect(() => {
    if (!verzeichnis.data) return;
    onMitglieder(
      gate.stepId,
      verzeichnis.data.map((m) => m.userId),
    );
  }, [verzeichnis.data, gate.stepId, onMitglieder]);

  // ⚠ `gate.anzahl` gilt für den GESPEICHERTEN Stand. Sobald die Wahl davon abweicht, wäre sie
  // eine Aussage über etwas anderes als das, was auf dem Schirm steht. Gerechnet wird dann
  // NUR eine getroffene Personen-Wahl (MED-3: die Rollen-Grösse kennt allein der Master);
  // sonst bleibt der Satz ohne Zahl.
  const abweichend = !gleicheWahl(wahl, gespeichert);
  const anzahl = abweichend
    ? aufgeloesteAnzahl(wahl, inhaberAn, ownerId, bekannt)
    : gate.anzahl;

  const fakten = {
    rolle: gate.rolle,
    anzahl,
    inhaberGewaehlt: inhaberAn,
    personenGewaehlt:
      personenWahl && wahl.personen !== undefined
        ? Array.isArray(wahl.personen)
          ? wahl.personen.length
          : null
        : gate.personenGewaehlt,
  };
  const quorum = quorumSatz(gate.quorum);
  // MED-4: eine GESPEICHERTE Unterbesetzung ist kein Eingabefehler dieser Sitzung — sie steht
  // als Hinweis an ihrer Zeile und sperrt nichts (die Bereitschaft meldet sie als Lücke).
  const hinweis =
    personenWahl && !beruehrtHier && Array.isArray(gespeichert?.personen)
      ? quorumFehlt(gate.quorum, gewaehlteAnzahl(gate, gespeichert, ownerId, bekannt))
      : null;

  // Bereits Gewählte stehen als Chip da — sie gehören nicht noch einmal in die Auswahl.
  // Rollen-Mitglieder kommen ZUERST (und tragen die Markierung); das SUCHEN übernimmt das
  // Primitiv, das über beide Zeilen sucht.
  const waehlbar = liste
    .filter((m) => !gewaehltePersonen.includes(m.userId))
    .sort((a, b) =>
      a.mitgliedDerRolle === b.mitgliedDerRolle
        ? a.anzeigeName.localeCompare(b.anzeigeName)
        : a.mitgliedDerRolle
          ? -1
          : 1,
    );

  const setzePersonen = (personen: string[] | null) => onChange({ ...wahl, personen });

  // ⚠ MED-D: EIN Baustein für beide Fehl-Zustände (Rückfall UND „nicht abrufbar"). Ein
  // rollenloser Schritt hat gar keinen Rückfall — ohne Wiederholung wäre er nach einem
  // einzelnen transienten Fehler für die ganze Sitzung tot (`retry: false` lässt den Abruf
  // bewusst kein zweites Mal laufen, damit der Rückfall sofort greift).
  // ⚠ L-H: während des Nachladens gesperrt, mit Rückmeldung — sonst klickt jemand dreimal
  // und hält den ausbleibenden Wechsel für einen zweiten Fehler.
  const wiederholen = (
    <button
      type="button"
      className="underline disabled:no-underline disabled:opacity-60"
      disabled={verzeichnis.isFetching}
      onClick={() => void verzeichnis.refetch()}
      data-testid={`instance-gate-${gate.stepId}-verzeichnis-retry`}
    >
      {verzeichnis.isFetching ? WIEDERHOLEN_LAEUFT_TEXT : WIEDERHOLEN_TEXT}
    </button>
  );

  return (
    <div className="rounded-md border border-border p-3" data-testid={`instance-gate-${gate.stepId}`}>
      <div className="text-[13px] font-semibold text-text">{gate.stepName || gate.stepId}</div>
      <p className="mt-0.5 text-[12.5px] text-text2" data-testid={`instance-gate-${gate.stepId}-satz`}>
        Hier entscheidet: {entscheiderSatz(fakten)}
      </p>
      {quorum && (
        <p className="text-[12px] text-text3" data-testid={`instance-gate-${gate.stepId}-quorum`}>
          {quorum}
        </p>
      )}

      {/* Nichts freigegeben ⇒ reine Fakten-Zeile. Ein gesperrtes Kästchen behauptete, hier
          gäbe es etwas zu entscheiden — es gibt hier nichts. */}
      {!inhaberSichtbar && !personenWahl ? (
        <p className="mt-1.5 text-[12px] text-text3" data-testid={`instance-gate-${gate.stepId}-fest`}>
          {/* Ohne Rolle heisst es NICHT „die Vorlage lässt nichts zu": dort ist der Inhaber
              die Vorgabe des Modells. Wer das nicht liest, sucht einen Schalter, den es nie gab. */}
          {keineWahlText(gate.rolle !== null)}
        </p>
      ) : (
        <div className="mt-2 space-y-2">
          {inhaberSichtbar && (
            <label className="flex items-start gap-2.5">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 shrink-0 accent-accent"
                checked={inhaberAn}
                // ⚠ HIGH-1: gesetzt statt gewählt — abwählen wäre folgenlos (der Master ruft
                // ihn trotzdem) und die Karte bliebe dauerhaft dirty. Die Sperre hängt an
                // DERSELBEN Ableitung, die auch `bereinigeWahl` benutzt: was nicht bedienbar
                // ist, geht auch nicht über die Leitung.
                disabled={!darfEdit || !inhaberWahl}
                onChange={(e) => onChange({ ...wahl, inhaber: e.target.checked })}
                data-testid={`instance-gate-${gate.stepId}-inhaber`}
              />
              <span className="text-[12.5px] text-text">
                Setup-Inhaber entscheidet selbst
                {inhaberZwang && (
                  <span
                    className="ml-2 text-[11.5px] text-text3"
                    data-testid={`instance-gate-${gate.stepId}-inhaber-zwang`}
                  >
                    {INHABER_ZWANG_TEXT}
                  </span>
                )}
              </span>
            </label>
          )}

          {personenWahl && (
            <>
              <label className="flex items-start gap-2.5">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 shrink-0 accent-accent"
                  checked={eingeschraenkt}
                  disabled={!darfEdit}
                  // Aus dem Haken raus heisst `null` = „keine namentliche Wahl" — dann gilt
                  // wieder die Rolle (bzw. am rollenlosen Schritt: niemand ausser dem
                  // Inhaber). `null` und „kein Eintrag" sagen dasselbe (#331/L-E).
                  //
                  // ⚠⚠ HIGH-A: mit dem Einschalten fällt am rollenlosen Schritt der
                  // Inhaber-ZWANG — er wird zur Wahl. Sein bisheriger Zustand wird deshalb
                  // MATERIALISIERT, damit der Patch die Absicht trägt; ohne das fiele der
                  // Inhaber still aus der Menge (die Karte zeigte ihn, der Master nicht).
                  onChange={(e) =>
                    onChange(
                      e.target.checked
                        ? { ...wahl, personen: [], ...(inhaberZwang ? { inhaber: inhaberAn } : {}) }
                        : { ...wahl, personen: null },
                    )
                  }
                  data-testid={`instance-gate-${gate.stepId}-nur-personen`}
                />
                {/* ⚠ Der Text sagt die ERSETZEN-Semantik: eine Wahl tritt AN DIE STELLE der
                    Rolle. „zusätzlich zu" wäre die häufigste Fehlannahme. */}
                <span className="text-[12.5px] text-text">{personenWahlLabel(gate.rolle !== null)}</span>
              </label>

              {eingeschraenkt && (
                <div className="ml-7 space-y-2">
                  {gewaehltePersonen.length > 0 && (
                    <ul className="flex flex-wrap gap-2" data-testid={`instance-gate-${gate.stepId}-chips`}>
                      {gewaehltePersonen.map((id) => {
                        // ⚠ Nur bei GELESENEM Adressbuch lässt sich überhaupt etwas sagen.
                        // Zwei verschiedene Aussagen, die man nicht verwechseln darf:
                        //  · NICHT im Adressbuch ⇒ die Person ist nicht mehr aktiv/intern.
                        //    Das ist ein PROBLEM (der Master schneidet sie beim Auflösen weg).
                        //  · im Adressbuch, aber nicht in der Rolle ⇒ seit #331 völlig in
                        //    Ordnung. Reine Information, kein Fehler — deshalb dezent.
                        const unbekannt = bekannt !== null && !bekannt.includes(id);
                        const nichtInRolle = !unbekannt && markierungMoeglich && !inRolle.has(id);
                        return (
                          <li
                            key={id}
                            className={`flex items-center gap-2 rounded-md border px-2.5 py-1 ${
                              unbekannt ? "border-warn-border" : "border-border"
                            }`}
                            data-testid={`instance-gate-${gate.stepId}-chip-${id}`}
                          >
                            {/* Kein Name ⇒ die Kennung, nie ein erfundener Klarname. */}
                            <span className="text-[12.5px] text-text">{nameNachId.get(id) ?? id}</span>
                            {unbekannt && (
                              <span
                                className="text-[11px] text-warn"
                                data-testid={`instance-gate-${gate.stepId}-chip-${id}-unbekannt`}
                              >
                                {CHIP_NICHT_VERFUEGBAR}
                              </span>
                            )}
                            {nichtInRolle && (
                              <span
                                className="text-[11px] text-text3"
                                data-testid={`instance-gate-${gate.stepId}-chip-${id}-nicht-in-rolle`}
                              >
                                {CHIP_NICHT_IN_ROLLE}
                              </span>
                            )}
                            {darfEdit && (
                              <button
                                type="button"
                                className="text-text3 hover:text-danger"
                                title="Aus der Auswahl nehmen"
                                onClick={() => setzePersonen(gewaehltePersonen.filter((x) => x !== id))}
                                data-testid={`instance-gate-${gate.stepId}-entfernen-${id}`}
                              >
                                <X className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}

                  {/* ⚠ Der Rückfall ist kein stiller Notbehelf: er bietet WENIGER an, als das
                      Modell erlaubt. Wer das nicht liest, sucht eine Person, die es gibt. */}
                  {/* ⚠ L-I: ohne gelesenes Adressbuch stehen oben Kennungen statt Namen —
                      das wird gesagt, statt es den Betrachter raten zu lassen. */}
                  {/* ⚠ L-A: nur wenn WIRKLICH ein Name fehlt. Im Rückfall sind die
                      Rollen-Mitglieder auflösbar — der Satz stünde sonst über lauter
                      Klarnamen und behauptete ein Problem, das es nicht gibt.
                      ⚠ LOW-1 (Fix-Runde 4): und nur, solange das Adressbuch UNBEKANNT ist.
                      Ist es gelesen und eine Person fehlt darin, ist sie gelöscht — das sagt
                      der Chip-Marker „nicht mehr verfügbar" genau; ein zweiter Satz über
                      „nicht auflösbare Namen" schöbe es auf eine Störung. */}
                  {bekannt === null && gewaehltePersonen.some((id) => !nameNachId.has(id)) && (
                    <p className="text-[12px] text-text3" data-testid={`instance-gate-${gate.stepId}-namen-unbekannt`}>
                      {NAMEN_UNBEKANNT_TEXT}
                    </p>
                  )}

                  {nurRollenMitglieder && (
                    <p className="text-[12px] text-warn" data-testid={`instance-gate-${gate.stepId}-nur-rolle-hinweis`}>
                      Das Firmen-Adressbuch ist gerade nicht abrufbar — hier sind deshalb nur
                      Rollen-Mitglieder wählbar. {wiederholen}
                    </p>
                  )}

                  {adressbuchVerweigert ? (
                    // L-6: kein Vorgang für das Adressbuch ⇒ kein Picker, der nur in ein 403
                    // liefe. ⚠ MED-B: erst wenn der Kontext GELADEN ist — solange er lädt,
                    // steht hier nichts (zwei Erklärungen nebeneinander wären eine zu viel).
                    <p className="text-[12px] text-text3" data-testid={`instance-gate-${gate.stepId}-kein-adressbuch`}>
                      {KEIN_ADRESSBUCH_TEXT}
                    </p>
                  ) : !darfAdressbuch ? null : laedt ? (
                    <p className="text-[12px] text-text3" data-testid={`instance-gate-${gate.stepId}-mitglieder-laedt`}>
                      {PERSONEN_LADEN_TEXT}
                    </p>
                  ) : nichtAbrufbar ? (
                    // ⚠ MED-D: auch hier eine Wiederholung — ein rollenloser Schritt hat gar
                    // keinen Rückfall und wäre sonst für die ganze Sitzung tot.
                    <p className="text-[12px] text-danger" data-testid={`instance-gate-${gate.stepId}-mitglieder-fehler`}>
                      Die wählbaren Personen sind gerade nicht abrufbar. {wiederholen}
                    </p>
                  ) : darfEdit ? (
                    // Haus-Regel „Auswahl statt Tippen" — dasselbe Primitiv wie in der
                    // Rollen-Verwaltung (eine Optik, ein Verhalten, dieselben testid-Suffixe).
                    <AuswahlMitSuche
                      testId={`instance-gate-${gate.stepId}`}
                      oeffnenLabel="Person hinzufügen"
                      suchePlatzhalter="Person suchen…"
                      eintraege={waehlbar.map((m) => ({
                        id: m.userId,
                        primaer: m.anzeigeName,
                        // Die Markierung ist eine HILFE, keine Bedingung: gewählt werden darf
                        // jede interne Person (#331). Ohne auflösbare Rolle gibt es nichts zu
                        // markieren — der Master liefert dann überall `false` (MED-2).
                        sekundaer:
                          markierungMoeglich && m.mitgliedDerRolle && gate.rolle
                            ? rollenMitgliedLabel(gate.rolle)
                            : undefined,
                      }))}
                      // ⚠ L-4: „alle schon gewählt" ist etwas anderes als „es gibt niemanden".
                      leerText={
                        liste.length > 0
                          ? "Alle wählbaren Personen sind bereits gewählt."
                          : "Es gibt keine wählbaren Personen."
                      }
                      keinTrefferText="Keine weitere Person passt zur Suche."
                      onWaehlen={(userId) => setzePersonen([...gewaehltePersonen, userId])}
                    />
                  ) : null}

                  {/* Präventiv-Meldung AM FELD (Muster #326): der Master lehnte die Wahl mit
                      einem generischen 400 ab — hier steht vorher, woran es liegt. */}
                  {fehler && (
                    <p className="text-[12px] text-danger" data-testid={`instance-gate-${gate.stepId}-fehler`}>
                      {fehler}
                    </p>
                  )}
                  {/* …und der HINWEIS auf einen gespeicherten Stand, den hier niemand
                      angefasst hat: er sperrt nichts (MED-4). */}
                  {!fehler && hinweis && (
                    <p className="text-[12px] text-warn" data-testid={`instance-gate-${gate.stepId}-hinweis`}>
                      {hinweis}
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
